import type { User } from "./types";

export const ACCESS_TOKEN_KEY = "yara:access-token";
export const OFFLINE_USER_KEY = "yara:offline-user";
export const DEV_REFRESH_TOKEN_KEY = "yara:dev-refresh-token";

const PRIVATE_STORAGE_KEY_PREFIXES = [
  OFFLINE_USER_KEY,
  ACCESS_TOKEN_KEY,
  DEV_REFRESH_TOKEN_KEY,
  "yara:chat-history:",
  "yara:chat-scroll:",
] as const;

export function readStoredAccessToken(): string | null {
  if (typeof window === "undefined") return null;
  try {
    const token = window.sessionStorage.getItem(ACCESS_TOKEN_KEY);
    if (token || !import.meta.env.DEV) return token;
  } catch {
    if (!import.meta.env.DEV) return null;
  }
  try {
    return window.localStorage.getItem(ACCESS_TOKEN_KEY);
  } catch {
    return null;
  }
}

export function writeStoredAccessToken(token: string | null) {
  if (typeof window === "undefined") return;
  try {
    if (token) window.sessionStorage.setItem(ACCESS_TOKEN_KEY, token);
    else window.sessionStorage.removeItem(ACCESS_TOKEN_KEY);
  } catch {
    // Private browsing or constrained storage should not block auth.
  }
  if (import.meta.env.DEV) {
    try {
      if (token) window.localStorage.setItem(ACCESS_TOKEN_KEY, token);
      else window.localStorage.removeItem(ACCESS_TOKEN_KEY);
    } catch {
      // Private browsing or constrained storage should not block dev auth.
    }
  }
}

export function readStoredRefreshToken(): string | null {
  if (typeof window === "undefined" || !import.meta.env.DEV) return null;
  try {
    return window.localStorage.getItem(DEV_REFRESH_TOKEN_KEY);
  } catch {
    return null;
  }
}

export function writeStoredRefreshToken(token: string | null) {
  if (typeof window === "undefined" || !import.meta.env.DEV) return;
  try {
    if (token) window.localStorage.setItem(DEV_REFRESH_TOKEN_KEY, token);
    else window.localStorage.removeItem(DEV_REFRESH_TOKEN_KEY);
  } catch {
    // Private browsing or constrained storage should not block dev auth.
  }
}

export function readOfflineUser(): User | null {
  if (typeof window === "undefined") return null;
  try {
    const value = window.localStorage.getItem(OFFLINE_USER_KEY);
    return value ? JSON.parse(value) as User : null;
  } catch {
    return null;
  }
}

export function writeOfflineUser(user: User | null) {
  if (typeof window === "undefined") return;
  try {
    if (user) window.localStorage.setItem(OFFLINE_USER_KEY, JSON.stringify(user));
    else window.localStorage.removeItem(OFFLINE_USER_KEY);
  } catch {
    // Private browsing or constrained storage should not block auth.
  }
}

export function clearPrivateOfflineStorage() {
  if (typeof window === "undefined") return;
  try {
    for (const storage of [window.localStorage, window.sessionStorage]) {
      for (let index = storage.length - 1; index >= 0; index -= 1) {
        const key = storage.key(index);
        if (key && PRIVATE_STORAGE_KEY_PREFIXES.some((prefix) => key.startsWith(prefix))) {
          storage.removeItem(key);
        }
      }
    }
  } catch {
    // Best-effort cleanup; CacheStorage and query cache are cleared separately.
  }
}
